import { faBuilding } from "@fortawesome/free-regular-svg-icons";
import {
  faArrowRightFromBracket,
  faCertificate,
  faChevronLeft,
  faChevronRight,
  faCog,
  faColumns,
  faDashboard,
  faFileImport,
  faList,
  faPeopleGroup,
  faQuestion,
  type IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useKeycloak } from "@react-keycloak/web";
import type React from "react";
import { type FC, useCallback, useMemo } from "react";
import { NavLink } from "react-router";
import { AdminUserRole, CustomerUserRole, LeadAssessorUserRole } from "../../api/dto";
import { usePersistentState } from "../../util/usePersistentState";
import { Button } from "../form/Button";

const navigation: { link: string; icon: IconDefinition; text: string; allowedRoles: string[] }[] = [
  {
    link: "/companies",
    text: "Bedrijven",
    icon: faBuilding,
    allowedRoles: [AdminUserRole, LeadAssessorUserRole],
  },
  {
    link: "/",
    text: "Assessments",
    icon: faList,
    allowedRoles: [AdminUserRole, LeadAssessorUserRole],
  },
  {
    link: "/browse",
    text: "Bladeren",
    icon: faColumns,
    allowedRoles: [AdminUserRole, CustomerUserRole],
  },
  {
    link: "/export-import",
    text: "Import/Export",
    icon: faFileImport,
    allowedRoles: [AdminUserRole, LeadAssessorUserRole],
  },
  {
    link: "/assessors",
    text: "Assessors",
    icon: faPeopleGroup,
    allowedRoles: [AdminUserRole],
  },
  {
    link: "/certificates",
    text: "Certificaten",
    icon: faCertificate,
    allowedRoles: [AdminUserRole],
  },
  {
    link: "/dashboards",
    text: "Dashboards",
    icon: faDashboard,
    allowedRoles: [AdminUserRole],
  },
  {
    link: "/question-sets",
    text: "Vragenlijsten",
    icon: faQuestion,
    allowedRoles: [AdminUserRole, LeadAssessorUserRole],
  },
  {
    link: "/settings",
    text: "Instellingen",
    icon: faCog,
    allowedRoles: [AdminUserRole],
  },
];
export const AuthenticatedLayout: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { keycloak } = useKeycloak();
  const [isCollapsed, setIsCollapsed] = usePersistentState<boolean>("collapse_sidebar", false);
  const email = keycloak.tokenParsed?.["email"] as unknown as string;
  const roles = useMemo(
    () => keycloak.tokenParsed?.realm_access?.roles ?? [],
    [keycloak.tokenParsed],
  );
  const canUserAccessItem = useCallback(
    (item: {
      link: string;
      icon: IconDefinition;
      text: string;
      allowedRoles: string[];
    }): boolean => {
      return item.allowedRoles.some((role) => roles.includes(role));
    },
    [roles],
  );
  if (email && email.startsWith("assessor")) {
    return (
      <div className={"p-8 text-red-700"}>
        Geen toegang
        <div className={"mt-4"}>
          <Button
            type={"primary"}
            size={"sm"}
            text={"Uitloggen"}
            onClick={() => keycloak.logout()}
          />
        </div>
      </div>
    );
  }
  const linkStyle = (props: { isActive: boolean; isPending: boolean }) =>
    `h-12 flex items-center border-l-4 ${props.isActive ? "border-primary bg-brand-50 text-primary" : "border-transparent text-gray-500  hover:bg-gray-50 hover:border-primary"} px-5 font-medium`;
  return (
    <div className={"flex flex-col h-screen bg-slate-50"}>
      <nav className={"bg-primary text-white h-20 flex items-stretch justify-between"}>
        <div className={"flex justify-center items-center px-6"}>
          <h1 className={"font-bold text-xl"}>
            <img src={"/logo.svg"} alt={"Dijkgraaf Support"} className={"h-14"} />
          </h1>
        </div>
        <button
          className={"flex items-center hover:bg-[#0002] px-6"}
          onClick={() => keycloak.logout()}
        >
          <div className={"flex flex-col items-end justify-center mr-4"}>
            <small className={"leading-tight opacity-90"}>
              Ingelogd als {keycloak.idTokenParsed?.email ?? "-"}
            </small>
            <div className={"leading-tight"}>Uitloggen</div>
          </div>
          <FontAwesomeIcon icon={faArrowRightFromBracket} />
        </button>
      </nav>
      <div className={"flex-1 flex items-stretch "}>
        <aside
          className={`bg-white ${isCollapsed ? "w-16" : "w-48"} border-r border-gray-200 flex flex-col justify-between pt-8`}
        >
          {/* Navigation */}
          <div className={"flex flex-col"}>
            {navigation
              .filter((item) => canUserAccessItem(item))
              .map((item, i) => (
                <NavLink to={item.link} className={linkStyle} key={i}>
                  <span className={`w-5 text-center ${isCollapsed ? "" : "mr-3"}`}>
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                  {!isCollapsed && <span>{item.text}</span>}
                </NavLink>
              ))}
          </div>
          <button className={"hover:bg-gray-50 h-12"} onClick={() => setIsCollapsed((x) => !x)}>
            <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} />
          </button>
        </aside>
        <main className={"flex-1 overflow-y-scroll max-h-[calc(100vh-5rem)] mx-4"}>{children}</main>
      </div>
    </div>
  );
};
